import { WORKSPACE_RIGHT_PANEL_NAMES } from '#packages/constants';
import { components } from '#packages/stateManagement';
import { biLogger, hoc } from '#packages/util';
import {
  aiAssistantActionClick,
  aiAssistantErrorSrc38Evid2105,
  aiAssistantMessageSrc38Evid2103,
  aiAssistantPanelOpenSrc38Evid2101,
  aiAssistantTypingConsoleActionSrc38Evid2104,
} from '@wix/bi-logger-editor-data/v2';
import {
  useChatHttpClient,
  useChatServiceProvider,
} from '@wix/chat-service-sdk';
import type { OnWidgetAction } from '@wix/chat-widget-common-widgets';
import type { ChatMessageData } from '@wix/chat-widget-ui-lib';
import { ChatWidget, SidebarEnvironment } from '@wix/chat-widget-ui-lib';
import { createHttpClient } from '@wix/http-client';
import experiment from 'experiment';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import {
  IS_FIRST_ASSISTANT_MESSAGE,
  mapDispatchToProps,
  mapStateToProps,
} from './aiAssistantMapper';
import type { AiAssistantProps } from './aiAssistantTypes';
import {
  biWidgetActionMapper,
  getUserMessageCount,
  getWidgetMessage,
} from './helpers';
import { getEditorWidgets } from './widgets';
import type { ACTIONS } from './widgets/constants';

const AiAssistantSidebar: FC<AiAssistantProps> = ({
  config,
  closeAiAssistant,
  setToUserPreferences,
  isFirstMessage,
  appToken,
}) => {
  const httpClientWithToken = useMemo(
    () =>
      createHttpClient({
        getAppToken: () => appToken,
      }),
    [appToken],
  );

  const chatClient = useChatHttpClient(httpClientWithToken);
  const [isConversationStarted, setIsConversationStarted] = useState(false);

  const { createConversation, sendUserMessage, messages, statusMessage, logs } =
    useChatServiceProvider({
      chatClient,
      domainIds: config.domainIds,
      onFunctionCall: (name) => {
        return { success: true, response: { name } };
      },
      enableLogging: experiment.isOpen('se_aiAssistantLogs'),
    });

  const onWidgetAction: OnWidgetAction = (widgetName, data) => {
    config.onWidgetAction?.(widgetName, data);

    biWidgetActionMapper({
      assistantName: config.assistantName,
      origin: config.origin,
      widgetName,
      data,
    })[data.action as keyof typeof ACTIONS]?.();
  };

  const widgets = getEditorWidgets({
    send: async (content: string): Promise<void> => {
      config.onSendMessage?.(content);
      sendUserMessage(content);
      biLogger.report(
        aiAssistantMessageSrc38Evid2103({
          target: config.assistantName,
          role: 'user',
          isCustom: false,
          text: content,
        }),
      );
    },
    emitSideEffect: () => {},
    onWidgetAction,
    theme: config.theme,
  });

  const startConversation = useCallback(async () => {
    setIsConversationStarted(false);
    await createConversation({
      origin: config.origin,
      ...(config.firstUserMessage && {
        firstUserMessage: config.firstUserMessage,
      }),
      ...(config.firstAssistantMessageContent && {
        firstAssistantMessageContent: config.firstAssistantMessageContent,
      }),

      tools: config.tools,
    });
    setIsConversationStarted(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createConversation]);

  const onClearChat = () => {
    startConversation();

    biLogger.report(
      aiAssistantActionClick({
        actionGroup: config.assistantName,
        actionName: 'Clear Chat',
      }),
    );
  };

  const onSendMessage = (message: ChatMessageData) => {
    sendUserMessage(message.content);
    biLogger.report(
      aiAssistantMessageSrc38Evid2103({
        target: config.assistantName,
        role: message.role,
        isCustom: true,
        text: message.content,
      }),
    );

    config.onSendMessage?.(message.content);
  };

  const onUserStartTyping = (message: string) => {
    biLogger.report(
      aiAssistantTypingConsoleActionSrc38Evid2104({
        target: config.assistantName,
        text: message,
        action: 'start type',
      }),
    );

    config.onUserStartTyping?.(message);
  };

  const onError = (error: Error) => {
    biLogger.report(
      aiAssistantErrorSrc38Evid2105({
        origin: config.origin,
        p1: `${error}`,
      }),
    );
  };

  const isFirstMessageSent = useRef(false);

  useEffect(() => {
    if (!isFirstMessageSent.current) {
      isFirstMessageSent.current = true;
      startConversation();

      biLogger.report(
        aiAssistantPanelOpenSrc38Evid2101({
          isFirstMessage,
          target: config.assistantName,
          panel_name: WORKSPACE_RIGHT_PANEL_NAMES.AI_ASSISTANT,
          origin: config.origin,
        }),
      );

      setToUserPreferences(IS_FIRST_ASSISTANT_MESSAGE, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startConversation]);

  const onClose = () => {
    config.onCloseChat?.({
      userMessagesCount: getUserMessageCount(messages),
    });
    closeAiAssistant();
  };

  const chatMessages = useMemo(
    () => [
      ...(config.firstWidget ? [getWidgetMessage(config.firstWidget)] : []),
      ...messages,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages],
  );

  useEffect(() => {
    if (chatMessages.length === 0) return;

    const lastMessage: ChatMessageData = chatMessages[chatMessages.length - 1];

    if (!lastMessage.content || lastMessage.role !== 'assistant') return;

    biLogger.report(
      aiAssistantMessageSrc38Evid2103({
        target: config.assistantName,
        role: lastMessage.role,
        text: lastMessage.content,
      }),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages]);

  useEffect(() => {
    if (experiment.isOpen('se_aiAssistantLogs')) {
      console.log('AI Assistant logs:', logs); // eslint-disable-line
    }
  }, [logs]);

  return (
    <SidebarEnvironment
      title={config.assistantName}
      onClean={onClearChat}
      onClose={onClose}
      isFooterVisible={false}
    >
      {isConversationStarted && (
        <ChatWidget
          chatMessages={chatMessages}
          widgets={widgets}
          onSendMessage={onSendMessage}
          statusMessage={statusMessage}
          assistantName={config.assistantName}
          onUserStartTyping={onUserStartTyping}
          onWidgetAction={config.onWidgetAction}
          messageToSendPlaceholder={config.messagePlaceholder}
          onError={onError}
        />
      )}
    </SidebarEnvironment>
  );
};
const {
  connect,
  STORES: { EDITOR_API },
} = hoc;

const ConnectedAiAssistantSidebar = components.hoc.compPanel(
  connect(
    EDITOR_API,
    mapStateToProps,
    mapDispatchToProps,
  )(memo(AiAssistantSidebar)),
);

export default ConnectedAiAssistantSidebar;
